import React, {useCallback, useEffect, useRef, useState} from 'react';
import IntakeScreen from './IntakeScreen';
import ReviewScreen from './ReviewScreen';
import AnalysingDialog from './AnalysingDialog';
import ErrorPanel from './ErrorPanel';
import {generationUrl, getJSON, postJSON} from './api';

const RUNNING = ['classifying', 'extracting'];
const CLIENT_GUARD_MS = 190 * 1000; // the server's 180 s step timeout is authoritative (FR-015)

// AI deal form assistant (specs/018-ai-deal-form-assistant): Screen 1 → loading dialog → Screen 2 or the form.
export default function DealIntakeApp(props) {
    const {i18n, limits, urls, poll_interval_ms: pollInterval, progress_step_seconds: stepSeconds} = props;
    const t = i18n.intake;

    const [form, setForm] = useState({
        description: props.description || '',
        attachments: props.attachments || [],
        urls: props.benchmark_urls && props.benchmark_urls.length ? props.benchmark_urls : [''],
    });
    const [dealUuid, setDealUuid] = useState(props.deal_uuid);
    const [screen, setScreen] = useState('intake');
    const [generation, setGeneration] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [progressIndex, setProgressIndex] = useState(0);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState([]);
    const openedAt = useRef(0);

    const sentences = t.progress || [];

    const openDialog = () => {
        openedAt.current = Date.now();
        setProgressIndex(0); // the sentences start again every time the dialog opens (FR-014)
        setDialogOpen(true);
    };

    const handleState = useCallback((state) => {
        setGeneration(state);
        if (state.state === 'ready' && state.redirect_url) {
            window.location.assign(state.redirect_url);
        } else if (state.state === 'review') {
            setDialogOpen(false);
            setScreen('review');
        } else if (state.state === 'failed') {
            setDialogOpen(false);
            setScreen('error');
        }
    }, []);

    // Progress sentences: advance every stepSeconds, stop on the last one.
    useEffect(() => {
        if (!dialogOpen) return undefined;
        const timer = setInterval(() => {
            setProgressIndex((index) => Math.min(index + 1, Math.max(sentences.length - 1, 0)));
        }, stepSeconds * 1000);
        return () => clearInterval(timer);
    }, [dialogOpen, stepSeconds, sentences.length]);

    // Poll while an AI step is running.
    useEffect(() => {
        if (!dialogOpen || !dealUuid || !generation || !RUNNING.includes(generation.state)) return undefined;
        const timer = setTimeout(async () => {
            if (Date.now() - openedAt.current > CLIENT_GUARD_MS) {
                handleState({state: 'failed'});
                return;
            }
            const {ok, data} = await getJSON(generationUrl(dealUuid));
            handleState(ok && data ? data : {state: 'failed'});
        }, pollInterval);
        return () => clearTimeout(timer);
    }, [dialogOpen, dealUuid, generation, pollInterval, handleState]);

    const payload = () => ({
        deal_uuid: dealUuid,
        description: form.description,
        benchmark_urls: form.urls,
        deal_attachment_ids: form.attachments.map((a) => a.id),
    });

    const rememberDraft = (uuid) => {
        setDealUuid(uuid);
        const url = new URL(window.location.href);
        url.searchParams.set('deal_uuid', uuid);
        window.history.replaceState({}, '', url);
    };

    const onGenerate = async () => {
        setBusy(true);
        setErrors([]);
        const {ok, data} = await postJSON(urls.create, payload());
        setBusy(false);
        if (!ok) {
            setErrors((data && data.errors) || [t.error_message]);
            return;
        }
        rememberDraft(data.deal_uuid);
        openDialog();
        handleState(data.generation);
    };

    const onSkip = async () => {
        setBusy(true);
        const {ok, data} = await postJSON(urls.skip, payload());
        if (ok && data.redirect_url) {
            window.location.assign(data.redirect_url);
            return;
        }
        setBusy(false);
        setErrors((data && data.errors) || [t.error_message]);
    };

    const onApprove = async (draft, labels) => {
        setBusy(true);
        setErrors([]);
        const {ok, data} = await postJSON(generationUrl(dealUuid, 'approve'),
            {draft_description: draft, confirmed_categories: labels});
        setBusy(false);
        if (!ok) {
            setErrors((data && data.errors) || [t.error_message]);
            return;
        }
        openDialog();
        handleState(data);
    };

    const onRetry = async () => {
        setBusy(true);
        const {ok, data} = await postJSON(generationUrl(dealUuid, 'retry'));
        setBusy(false);
        setScreen(generation?.failed_step === 'extraction' ? 'review' : 'intake');
        if (!ok) {
            setScreen('error');
            return;
        }
        openDialog();
        handleState(data);
    };

    const onBack = () => {
        setErrors([]);
        setScreen('intake');
    };

    return (
        <div className="deal-intake">
            {screen === 'intake' && (
                <IntakeScreen t={t} form={form} setForm={setForm} limits={limits}
                              acceptedExtensions={props.accepted_extensions} uploadUrl={urls.upload}
                              errors={errors} busy={busy} onGenerate={onGenerate} onSkip={onSkip}
                              onUploadError={(message) => setErrors([message])}/>
            )}
            {screen === 'review' && generation?.review && (
                <ReviewScreen key={JSON.stringify(generation.review)} t={i18n.review} review={generation.review}
                              maxSkus={limits.max_skus} busy={busy} errors={errors}
                              onApprove={onApprove} onBack={onBack} onSkip={onSkip}/>
            )}
            {screen === 'error' && <ErrorPanel t={t} busy={busy} onRetry={onRetry} onSkip={onSkip}/>}
            <AnalysingDialog open={dialogOpen} title={t.dialog_title} sentences={sentences} index={progressIndex}/>
        </div>
    );
}
